'use client';

import { useEffect, useState } from 'react';
import { marketplaceApi } from '@/api';
import { Users, Stethoscope, Star, Globe2 } from 'lucide-react';

const FALLBACK_STATS = {
  totalDoctors: 500,
  totalSpecialties: 35,
  averageRating: 4.9,
  totalConsultations: 30000,
};

function formatConsultations(value: number): string {
  if (!value || value <= 0) return '+30 mil';
  if (value >= 1000) {
    const divided = value / 1000;
    if (divided >= 10) return `+${Math.round(divided)} mil`;
    return `+${divided.toFixed(1).replace('.', ',')} mil`;
  }
  return `+${value}`;
}

function formatDoctors(value: number): string {
  if (!value || value <= 0) return '+500';
  return `+${value}`;
}

export function TrustStats() {
  const [stats, setStats] = useState(FALLBACK_STATS);

  useEffect(() => {
    let isMounted = true;
    const fetchStats = async () => {
      try {
        const data = await marketplaceApi.getHome();
        const s = data.statistics;
        if (isMounted && s && s.totalDoctors > 0) {
          setStats({
            totalDoctors: s.totalDoctors || FALLBACK_STATS.totalDoctors,
            totalSpecialties: s.totalSpecialties || FALLBACK_STATS.totalSpecialties,
            averageRating: s.averageRating || FALLBACK_STATS.averageRating,
            totalConsultations: s.totalConsultations || FALLBACK_STATS.totalConsultations,
          });
        }
      } catch {
        // Keeps fallback stats smoothly without breaking UI
      }
    };
    fetchStats();
    return () => {
      isMounted = false;
    };
  }, []);

  const statItems = [
    {
      value: formatConsultations(stats.totalConsultations),
      label: 'pacientes atendidos',
      icon: Users,
    },
    {
      value: formatDoctors(stats.totalDoctors),
      label: 'médicos credenciados',
      icon: Stethoscope,
    },
    {
      value: `${stats.averageRating.toFixed(1).replace('.', ',')}`,
      hasStars: true,
      label: 'avaliação dos pacientes',
      icon: Star,
    },
    {
      value: 'Todo o Brasil',
      label: 'cobertura por telemedicina',
      icon: Globe2,
    },
  ];

  return (
    <section className="py-8 bg-card border-b border-border transition-colors">
      <div className="container-landing">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
          {statItems.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>

                <div>
                  <div className="flex items-center justify-center sm:justify-start gap-1.5">
                    <strong className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight">
                      {stat.value}
                    </strong>
                    {stat.hasStars && (
                      <div className="flex items-center text-amber-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-medium text-muted-foreground block mt-0.5">
                    {stat.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
